import {
  cargoSubtype,
  FreightTypeEntity,
  getObjectId, isStringUndefinedOrBlank,
  MeterEntity,
  Quantity,
  TimeGroup,
  toTimeGroup,
  TransportCategory,
  transportCategory
} from "@ots-frontend/ots-data";
import {useForm} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Divider, Group, Modal, ModalProps, NumberInput, Textarea} from "@mantine/core";
import {useCallback, useMemo} from "react";
import {DateInput} from "../../date/DateInput/DateInput";
import {TimeGroupSelect} from "../../select/entity/TimeGroupSelect/TimeGroupSelect";
import {UnitOfMeasureSelect} from "../../select/entity/UnitOfMeasureSelect/UnitOfMeasureSelect";
import {FreightTypeSelect} from "../../select/entity/FreightTypeSelect/FreightTypeSelect";
import {MeterSelect} from "../../select/entity/MeterSelect/MeterSelect";

interface MeterFlowVolumeFormProps {
  options?: MeterFlowVolumeFormOptions;
  defaultValues?: Partial<MeterFlowVolumeFormValues>;
  onSubmit?: (values: MeterFlowVolumeFormValues) => void;
  submitting?: boolean;
}

export interface MeterFlowVolumeFormOptions {
  // props here
  regionId?: string
  category?: TransportCategory
}

export interface MeterFlowVolumeFormValues {
  timeGroup: TimeGroup
  date: Date
  meter: MeterEntity | string
  quantity: Quantity;
  quantityOverride?: number
  quantityOverrideComment?: string
  freightType: FreightTypeEntity | string;
}

const getDefaultValues = (defaultValues: Partial<MeterFlowVolumeFormValues> | undefined) => {
  return {
    timeGroup: defaultValues?.timeGroup?.code,
    date: defaultValues?.date,
    meter: getObjectId(defaultValues?.meter),
    quantity: defaultValues?.quantity?.number,
    quantityUom: defaultValues?.quantity?.units,
    quantityOverride: defaultValues?.quantityOverride,
    quantityOverrideComment: defaultValues?.quantityOverrideComment,
    freightType: getObjectId(defaultValues?.freightType)
  }
}

export const MeterFlowVolumeForm = (props: MeterFlowVolumeFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const {t} = useTranslation(["common"]);

  const form = useForm({
    initialValues: getDefaultValues(defaultValues),
    validate: {
      quantityOverrideComment: (value, values) =>
        (values.quantityOverride && isStringUndefinedOrBlank(value)) ? t("common.error.required", "Required") : undefined
    }
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        timeGroup: toTimeGroup(values.timeGroup!),
        date: values.date!,
        meter: values.meter!,
        quantity: {
          number: values.quantity!,
          units: values.quantityUom!
        },
        quantityOverride: values.quantityOverride,
        quantityOverrideComment: values.quantityOverrideComment,
        freightType: values.freightType!,
      });
    }
  }, [form, onSubmit]);

  const quantityOverrideRequired = useMemo(() => {
    const quantityOverride = form.getTransformedValues().quantityOverride
    return typeof quantityOverride !== 'undefined'
  }, [form])

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <TimeGroupSelect
        label={t("common.entity.meterflowvolume.form.field.timegroup", "Time Period")}
        placeholder={t<string>("common.entity.meterflowvolume.form.field.timegroup.placeholder", "Time Period")}
        styles={{wrapper: {width: "100%"}}}
        withAsterisk
        required
        clearable
        {...form.getInputProps("timeGroup")} />
      <DateInput
        valueFormat="MM/DD/YYYY"
        label={t("common.entity.meterflowvolume.form.field.date", "Date")}
        placeholder={t<string>("common.entity.meterflowvolume.form.field.date.placeholder", "Date")}
        styles={{wrapper: {width: "50%"}}}
        withAsterisk
        required
        clearable
        {...form.getInputProps("date")} />
      <Divider my="sm" variant="dotted"/>
      <MeterSelect
        label={t("common.entity.meterflowvolume.form.field.meter", "Meter")}
        placeholder={t<string>("common.entity.meterflowvolume.form.field.meter.placeholder", "Meter")}
        selector={{
          regionId: options?.regionId,
        }}
        clearable
        withAsterisk
        required
        {...form.getInputProps("meter")} />
      <Divider my="sm" variant="dotted"/>
      <FreightTypeSelect
        label={t("common.entity.meterflowvolume.form.field.freighttype", "Freight Type")}
        placeholder={t<string>("common.entity.meterflowvolume.form.field.freighttype.placeholder", "Freight Type")}
        selector={{
          regionId: options?.regionId,
          cargoSubtype: options?.category === transportCategory.oil ? cargoSubtype.oil : undefined
        }}
        clearable
        withAsterisk
        required
        {...form.getInputProps("freightType")} />
      <Group grow>
        <NumberInput
          label={t("common.entity.meterflowvolume.form.field.quantity", "Quantity")}
          placeholder={t<string>("common.entity.meterflowvolume.form.field.quantity.placeholder", "Quantity")}
          precision={5}
          parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
          withAsterisk
          required
          {...form.getInputProps("quantity")} />
        <UnitOfMeasureSelect
          label={t("common.entity.meterflowvolume.form.field.uom", "Units")}
          placeholder={t<string>("common.entity.meterflowvolume.form.field.uom.placeholder", "Units")}
          clearable
          withAsterisk
          required
          {...form.getInputProps("quantityUom")} />
      </Group>
      <Divider my="sm" variant="dotted"/>
      <NumberInput
        label={t("common.entity.meterflowvolume.form.field.quantityoverride", "Quantity Override")}
        placeholder={t<string>("common.entity.meterflowvolume.form.field.quantityoverride.placeholder", "Quantity Override")}
        precision={5}
        parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
        {...form.getInputProps("quantityOverride")} />
      <Textarea
        label={t("common.entity.meterflowvolume.form.field.quantityoverridecomments", "Quantity Override Comments")}
        placeholder={t<string>("common.entity.meterflowvolume.form.field.quantityoverridecomments.placeholder", "Explain the quantity override")}
        withAsterisk={quantityOverrideRequired}
        required={quantityOverrideRequired}
        {...form.getInputProps("quantityOverrideComment")} />
      <Button type="submit" loading={submitting} fullWidth mt="xl">
        {t("common.action.submit", {ns: "common"})}
      </Button>
    </form>
  );
};

interface MeterFlowVolumeFormModalProps extends MeterFlowVolumeFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const MeterFlowVolumeFormModal = (props: MeterFlowVolumeFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    submitting,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <MeterFlowVolumeForm defaultValues={defaultValues} onSubmit={onSubmit} submitting={submitting}/>
    </Modal>
  );
};
